import type { GridApiPremium, GridDataSource, GridSortModel } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
  GRID_DEFAULT_PAGE_SIZE,
} from "@entrilia/shared/components/grid/gridHelper";
import { logError } from "@entrilia/shared/logging";
import type { WorkbookRun } from "../../../../api/types/workbookRuns.types";
import storage from "../../../../storage/storage";
import { useFilterContext } from "../../../common/filters/FilterContext";
import { sortAction } from "../../../common/filters/filterState";
import { getCheckedGridSx } from "../../../common/grid/gridStyles";
import { getColumnDefinitions } from "./workbookRunsGridDataProvider";
import WorkbookRunsToolbarActions from "./WorkbookRunsToolbarActions";

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
  selectedIds: string[];
  showToolbar: boolean;
  cancelingIds: string[];
  downloadingIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onDownloadRun: (id: string) => void;
  onDownloadRuns: (ids: string[]) => void;
  onCancelRuns: (ids: string[]) => void;
  onCancelRun: (run: WorkbookRun) => void;
  onDeleteRuns: (ids: string[]) => void;
  getRunPath: (id: string) => string;
}

const WorkbookRunsGrid = ({
  apiRef,
  dataSource,
  filterId,
  selectedIds,
  showToolbar,
  cancelingIds,
  downloadingIds,
  setSelectedIds,
  onDownloadRun,
  onDownloadRuns,
  onCancelRun,
  onCancelRuns,
  onDeleteRuns,
  getRunPath,
}: Props) => {
  const { filterState, setFilterState } = useFilterContext<WorkbookRun>();
  const sortModel = useMemo(() => (filterState.sorting ? [filterState.sorting] : []), [filterState.sorting]);
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const columns = useMemo(
    () =>
      getColumnDefinitions({
        downloadingIds,
        cancelingIds,
        getRunPath,
        onDownload: onDownloadRun,
        onCancel: onCancelRun,
        onDelete: onDeleteRuns,
      }),
    [downloadingIds, cancelingIds, getRunPath, onDownloadRun, onCancelRun, onDeleteRuns]
  );

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[WorkbookRunsGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  const handleSortModelChange = useCallback(
    (gridSortModel: GridSortModel) => {
      setFilterState(sortAction(gridSortModel));
    },
    [setFilterState]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Failed to load workbook runs" />;
  }

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedIds, setSelectedIds]}
      actions={
        <WorkbookRunsToolbarActions
          selectedIds={selectedIds}
          cancelingIds={cancelingIds}
          downloadingIds={downloadingIds}
          onDownloadRuns={onDownloadRuns}
          onDeleteRuns={onDeleteRuns}
          onCancelRuns={onCancelRuns}
        />
      }
    >
      <DataGrid<WorkbookRun>
        apiRef={apiRef}
        dataSource={dataSource}
        dataSourceCache={null}
        lazyLoading
        columns={columns}
        noRowsText="No workbook runs yet"
        shiftActionsLeft
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableColumnReorder
        rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
        checkboxSelection
        onRowSelectionModelChange={(selection) => setSelectedIds(getSelectedRowIdsFromModel(selection))}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        sx={getCheckedGridSx}
        disableMultipleColumnsSorting
        sortingMode="server"
        onDataSourceError={handleDataSourceError}
        paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default WorkbookRunsGrid;
